import React, { useState, useCallback } from 'react'
import Input from 'components/Form/Input'
import Icon from 'components/misc/Icon'
import styled from 'styled-components/macro'
import { Caption, Body2 } from 'components/layout/text'
import { vehicleTypes, vehicleColors, vehicleSubTypes } from 'util/vehicle'
import { useSelector } from 'react-redux'

export const vehicles = [
  {
    name: 'Two Door / Sedan',
    value: 'two_door',
  }, {
    name: 'Hatchback / SUV',
    value: 'hatchback_suv',
  }, {
    name: 'Minivan / Passenger Van',
    value: 'van',
  }, {
    name: 'Pickup Truck',
    value: 'pickup_truck',
  }, {
    name: 'Cargo Van',
    value: 'cargo_van',
  }, {
    name: 'Box Truck (10 - 15 ft)',
    value: 'box_truck',
  }, {
    name: 'Box Truck (16 - 25 ft)',
    value: 'large_box_truck',
  }, {
    name: 'Box Truck (26+ ft)',
    value: 'oversized_box_truck',
  }
]

export function getVehicleLabel(kind) {
  const vehicle = vehicles.find(({ value }) => value === kind)
  return vehicle ? vehicle.name : ''
}

const ColorOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 32.8rem;
  margin: 0 auto;
`

const ColorExample = styled.span`
  background-color: ${props => props.color};
  height: 2.4rem;
  width: 2.4rem;
  margin-left: 15px;
  margin-right: 8px;
  border-radius: 4px;
  border: solid 1px ${props => props.theme.colors.greyBase};
`

const ColorOptionButton = styled.div`
  width: 10.4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  border: 2px solid ${props => props.selected ? props.theme.colors.primaryBase : props.theme.colors.greyLighter};
  border-radius: 20px;
  background-color: ${props => props.theme.colors.greyLighter};
  margin-bottom: 8px;
  text-transform: capitalize;
  cursor: pointer;
`

const ShowAllColors = styled(Caption)`
  margin-top: 8px;
  text-align: center;
  color: ${props => props.theme.colors.primaryBase};
  font-weight: bold;
  cursor: pointer;
`

const Container = styled.div``

export const VehicleColorSelect = props => {
  const { onChange, color: currentColor } = props
  const [expanded, setExpanded] = useState(false)
  const handleViewAll = useCallback(() => {
    setExpanded(true)
  }, [])
  const handleSelect = useCallback(newSelected => {
    onChange('color', newSelected)
  }, [onChange])
  const colors = expanded ? vehicleColors : vehicleColors.slice(0, 6)
  return (
    <Container>
      <Input.Hidden {...props} value={currentColor || props.defaultValue} />
      <ColorOptionsContainer expanded={expanded}>
        {colors.map((color, index) =>
          <ColorOptionButton
            key={index}
            selected={color.label === currentColor}
            onClick={() => handleSelect(color.label)}
          >
            <ColorExample color={color.hex} />
            <Caption>{color.label}</Caption>
          </ColorOptionButton>
        )}
      </ColorOptionsContainer>
      { !expanded && <ShowAllColors onClick={handleViewAll}>View More Colors</ShowAllColors> }
    </Container>
  )
}

const TypeOptionsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
`

const TypeOptionButton = styled.div`
  width: 7.2rem;
  min-width: 7.2rem;
  height: 7.2rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.greyLighter};
  ${props => props.selected && `border: 2px solid ${props.theme.colors.primaryBase};`}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 4px;
`

const SubcategoryData = styled.div`
  text-align: left;
  position: absolute;
  top: 15px;
`

const Subcategories = ({ category, subcategory, onChange }) => {
  const handleChange = useCallback(event => {
    onChange('subcategory', event.target.value)
  }, [onChange])
  return (
    <Input.RadioGroup key={subcategory} name="subcategory" onChange={handleChange} defaultValue={subcategory}>
      {radioElementProps => (
        <>
          {vehicleSubTypes[category].map(sub => 
            <Input.RadioElement {...radioElementProps} value={sub.name}>
              {() => <SubcategoryData>
                <Body2>{sub.name}</Body2>
                <Caption emphasis="medium">{sub.desc}</Caption>
              </SubcategoryData>}
            </Input.RadioElement>)}
        </>
      )}
    </Input.RadioGroup>
  )
}

export const VehicleTypeSelect = props => {
  const { onChange, category, subcategory } = props
  const isMobile = useSelector(state => state.config.mediaSize === 'mobile')
  const handleCategorySelect = useCallback(newCategory => {
    onChange('category', newCategory)
  }, [onChange])
  return (
    <>
      <Input.Hidden name="category" value={category} />
      <Input.Hidden name="subcategory" value={subcategory} />
      { 
        isMobile 
        ? <>
            <TypeOptionsContainer>
              {vehicleTypes.slice(0, 3).map((v, i) => <TypeOptionButton 
                  key={i} 
                  selected={v.type === category}
                  onClick={() => handleCategorySelect(v.type)}
                >
                  <Icon symbol={v.icon} />
                  <Caption>{v.type}</Caption>
              </TypeOptionButton>)}
            </TypeOptionsContainer>
            <TypeOptionsContainer>
              {vehicleTypes.slice(3, 5).map((v, i) => <TypeOptionButton 
                  key={i} 
                  selected={v.type === category}
                  onClick={() => handleCategorySelect(v.type)}
                >
                  <Icon symbol={v.icon} />
                  <Caption>{v.type}</Caption>
              </TypeOptionButton>)}
            </TypeOptionsContainer>
          </>
        : <TypeOptionsContainer>
            {vehicleTypes.map((v, i) => <TypeOptionButton 
                key={i} 
                selected={v.type === category}
                onClick={() => handleCategorySelect(v.type)}
              >
                <Icon symbol={v.icon} />
                <Caption>{v.type}</Caption>
            </TypeOptionButton>)}
          </TypeOptionsContainer>
      }
      
      {
        category && <Subcategories 
          category={ category } 
          subcategory={ subcategory } 
          onChange={ onChange }
        />
      }
    </>
  )
}